import { AuthTokenResponse, FetchResult } from 'src/types/TApiQueries';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from 'src/api/utils/helpers';

// Social login ("Continue with Google / Microsoft"). Mirrors
// filespin/api/sso.py; design in
// filespin-saas-backend/docs/design/social-login-sso-plan.md.
//
// Flow from the UI's point of view:
//   1. GET /providers                -> which buttons to render
//   2. navigate to /{provider}/start -> the backend bounces through the IdP
//   3. the backend redirects back to /login/sso?code=..&next=..[&signup=1]
//   4. POST /exchange {code}         -> login envelope, or a signup prefill
//   5. (signup) POST /complete-signup -> login envelope
//
// The code is single-use and short-lived; never put the JWT in a URL.

export type SsoProvider = 'google' | 'microsoft' | string;

// login: the login page's buttons - an identity nobody owns is an error.
// signup: the signup page's buttons - the only way SSO may create an account.
export type SsoIntent = 'login' | 'signup';

export type SsoProvidersResponse = { providers: SsoProvider[] };

// Login envelope (same as POST /api/v1/login) plus `exp` (JWT expiry, epoch
// seconds) so the session length does not have to be guessed.
export type SsoLoginResponse = AuthTokenResponse & { exp?: number };

export type SsoSignupPrefill = {
  signup_required: true;
  provider: SsoProvider;
  email: string;
  first_name: string;
  last_name: string;
  industries: { value: string; label: string }[];
};

export type SsoExchangeResponse = SsoLoginResponse | SsoSignupPrefill;

export type SsoCompleteSignupRequest = {
  code: string;
  first_name: string;
  last_name: string;
  industry: string;
};

const BASE = `${config.hostUrl}/api/v1/auth/sso`;

// `attribution` (signup only): the signup page's utm_* parameters, ad click
// IDs and opt-out (src/utils/campaign.ts attributionParams) and URL.
// The backend keeps them in the SSO state, since the IdP round-trip drops the
// page's own query, and credits the new account with them.
export function ssoStartUrl(
  provider: SsoProvider,
  returnTo?: string,
  intent: SsoIntent = 'login',
  attribution?: { utm: URLSearchParams; page: string },
): string {
  const params = new URLSearchParams();
  if (returnTo) params.set('return_to', returnTo);
  if (intent === 'signup') {
    params.set('intent', 'signup');
    if (attribution) {
      attribution.utm.forEach((value, key) => params.set(key, value));
      params.set('page', attribution.page);
    }
  }
  const q = params.toString();
  return `${BASE}/${provider}/start${q ? `?${q}` : ''}`;
}

export function fetchSsoProviders(): Promise<FetchResult<SsoProvidersResponse>> {
  return performFetchWithLogging<SsoProvidersResponse>(`${BASE}/providers`, {
    method: 'GET',
    headers: getStandardHeaders(),
  });
}

export function exchangeSsoCode(code: string): Promise<FetchResult<SsoExchangeResponse>> {
  return performFetchWithLogging<SsoExchangeResponse>(`${BASE}/exchange`, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: JSON.stringify({ code }),
  });
}

export function completeSsoSignup(req: SsoCompleteSignupRequest): Promise<FetchResult<SsoLoginResponse>> {
  return performFetchWithLogging<SsoLoginResponse>(`${BASE}/complete-signup`, {
    method: 'POST',
    headers: getStandardHeaders(),
    credentials: 'include',
    body: JSON.stringify(req),
  });
}

export function isSsoSignupPrefill(r: SsoExchangeResponse): r is SsoSignupPrefill {
  return !!r && typeof r === 'object' && (r as SsoSignupPrefill).signup_required === true;
}
